import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Download, TrendingUp, Filter, Calendar } from 'lucide-react';
import AdminLayout from '../../../components/admin/AdminLayout';
import { useAdminAuth } from '../../../contexts/AdminAuthContext';

const RevenueReportsPage = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAdminAuth();
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month');
  const [serviceType, setServiceType] = useState('all');
  const [clientType, setClientType] = useState('all');
  const [groupBy, setGroupBy] = useState('none');
  const [revenues, setRevenues] = useState([]);

  useEffect(() => {
    setTimeout(() => {
      const mockRevenues = [
        { id: 'REV-001', date: '2024-12-01', client: 'محمد أحمد', clientType: 'فرد', service: 'استشارة قانونية', amount: 5000, paymentMethod: 'invoice' },
        { id: 'REV-002', date: '2024-12-03', client: 'شركة النور', clientType: 'شركة', service: 'تمثيل قضائي', amount: 15000, paymentMethod: 'contract' },
        { id: 'REV-003', date: '2024-12-05', client: 'فاطمة علي', clientType: 'فرد', service: 'صياغة عقد', amount: 3000, paymentMethod: 'invoice' }
      ];
      setRevenues(mockRevenues);
      setLoading(false);
    }, 500);
  }, [period, serviceType, clientType]);

  const filteredRevenues = revenues.filter(r => {
    if (serviceType !== 'all' && r.service !== serviceType) return false;
    if (clientType !== 'all' && r.clientType !== clientType) return false;
    return true;
  });

  const totalRevenue = filteredRevenues.reduce((sum, r) => sum + r.amount, 0);

  const handleExport = (format) => {
    alert(`تصدير تقرير الإيرادات بصيغة ${format}`);
  };

  if (loading) {
    return <AdminLayout><div className="flex items-center justify-center h-full"><div className="w-16 h-16 border-4 border-[#1E3A8A] border-t-transparent rounded-full animate-spin"></div></div></AdminLayout>;
  }

  return (
    <AdminLayout>
      <div className="p-6 max-w-7xl mx-auto">
        <div className="mb-6">
          <button onClick={() => navigate('/admin/finance/reports')} className="inline-flex items-center gap-2 text-gray-600 hover:text-[#1E3A8A] mb-4 transition-colors">
            <ArrowRight className="w-5 h-5" /><span className="font-semibold">العودة للتقارير</span>
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-black text-[#1E3A8A] mb-2 flex items-center gap-3">
                <TrendingUp className="w-8 h-8" /><span>تقارير الإيرادات</span>
              </h1>
              <p className="text-gray-600">تحليل مفصل للإيرادات حسب الفترة والخدمة</p>
            </div>
            {hasPermission('finance.reports.export') && (
              <div className="flex items-center gap-3">
                <button onClick={() => handleExport('PDF')} className="inline-flex items-center gap-2 px-4 py-2 border-2 border-[#1E3A8A] text-[#1E3A8A] rounded-lg font-bold hover:bg-blue-50 transition-all">
                  <Download className="w-4 h-4" /><span>PDF</span>
                </button>
                <button onClick={() => handleExport('Excel')} className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700 transition-all">
                  <Download className="w-4 h-4" /><span>Excel</span>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">الفترة</label>
              <select value={period} onChange={(e) => setPeriod(e.target.value)} className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none">
                <option value="day">اليوم</option>
                <option value="week">هذا الأسبوع</option>
                <option value="month">هذا الشهر</option>
                <option value="year">هذا العام</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">نوع الخدمة</label>
              <select value={serviceType} onChange={(e) => setServiceType(e.target.value)} className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none">
                <option value="all">جميع الخدمات</option>
                <option value="استشارة قانونية">استشارة قانونية</option>
                <option value="تمثيل قضائي">تمثيل قضائي</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">نوع العميل</label>
              <select value={clientType} onChange={(e) => setClientType(e.target.value)} className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none">
                <option value="all">الكل</option>
                <option value="فرد">فرد</option>
                <option value="شركة">شركة</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">تجميع حسب</label>
              <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)} className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none">
                <option value="none">بدون تجميع</option>
                <option value="service">نوع الخدمة</option>
                <option value="client">العميل</option>
              </select>
            </div>
          </div>
        </div>

        {/* Summary */}
        <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg p-6 mb-6 text-white">
          <h3 className="text-lg font-bold mb-2">إجمالي الإيرادات</h3>
          <p className="text-4xl font-black">{totalRevenue.toLocaleString()} ر.س</p>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b-2 border-gray-200">
              <tr>
                <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">التاريخ</th>
                <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">العميل</th>
                <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">الخدمة</th>
                <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">المبلغ</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredRevenues.map(r => (
                <tr key={r.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm">{r.date}</td>
                  <td className="px-4 py-3 text-sm font-semibold">{r.client}</td>
                  <td className="px-4 py-3 text-sm">{r.service}</td>
                  <td className="px-4 py-3 text-lg font-bold text-green-600">{r.amount.toLocaleString()} ر.س</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
};

export default RevenueReportsPage;